/**
 * ChallengeManageModal — painel do professor para um desafio (Fase 5).
 *  - Desempenho: adesão, conclusão, quem está perto/parado (GET /challenges/{id}/analytics)
 *  - Inscrever: busca server-side de alunos + inscrição em massa (POST /challenges/{id}/enroll)
 *  - Fotos: fotos de check-in enviadas no desafio, pro criador validar (GET /challenges/{id}/photos)
 * Autocontido (estilos inline) pra não inchar o Dashboard nem criar mais um CSS module.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api from "../services/api";
import { buildImageSrc } from "./userAdmin/userAdminHelpers";

const overlay = {
  position: "fixed", inset: 0, zIndex: 1300,
  background: "rgba(0,0,0,0.55)", display: "flex",
  alignItems: "center", justifyContent: "center", padding: 16,
};
const modal = {
  width: "100%", maxWidth: 560, maxHeight: "88vh", overflowY: "auto",
  background: "rgb(var(--surface-solid-rgb, 26, 39, 68))",
  border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: 20,
  color: "var(--text)",
};
const tabBtn = (on) => ({
  flex: 1, padding: "8px 10px", borderRadius: 10, cursor: "pointer",
  border: "1px solid " + (on ? "rgba(29,158,117,0.5)" : "rgba(255,255,255,0.12)"),
  background: on ? "rgba(29,158,117,0.15)" : "transparent",
  color: "var(--text)", fontWeight: 600, fontSize: 13,
});
const statCard = {
  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 12, padding: "12px 14px",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)",
  color: "var(--text)", fontSize: 14,
};

// Endereço das fotos de check-in. buildImageSrc mantém o /api da base: em
// produção REACT_APP_API_URL é "/api" e só /api/... chega no backend (que
// serve /uploads); sem o /api a foto caía no servidor do front e quebrava.
function buildUploadUrl(relativePath) {
  return buildImageSrc(relativePath, (process.env.REACT_APP_API_URL || "").replace(/\/+$/, ""));
}

function Stat({ label, value, accent }) {
  return (
    <div style={statCard}>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent || "var(--text)" }}>{value}</div>
      <div style={{ fontSize: 12, color: "var(--muted)" }}>{label}</div>
    </div>
  );
}

export default function ChallengeManageModal({ open, onClose, challenge, viewerRole }) {
  const challengeId = challenge?.challenge_id ?? challenge?.id;
  // Aluno criador de "Desafio com amigos" não tem alunos pra inscrever —
  // GET /users/students retorna vazio pra essa role (não expõe a base de
  // alunos da plataforma, ver docs/07-issues.md R225) — então a aba fica
  // sempre com "Nenhum aluno encontrado", confuso sem contexto. Só
  // professor/admin veem essa aba.
  // Inscrever é só do criador: participante de desafio "entre amigos" também
  // abre este modal (desempenho e fotos), mas não inscreve ninguém.
  const canEnroll = viewerRole !== "aluno" && challenge?.isCreator !== false;
  // "Exigir foto" só existe hoje no fluxo "Desafio com amigos" do aluno
  // (FriendChallengeModal) — desafio criado por professor/admin nunca tem
  // photo_required=True (a UI de criação deles ainda não expõe o toggle,
  // ver docs/07-issues.md R227), então a aba nunca teria nada pra mostrar.
  const canPhotos = viewerRole === "aluno" || !!challenge?.is_private_invite;
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [search, setSearch] = useState("");
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [enrolling, setEnrolling] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [photosLoaded, setPhotosLoaded] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const loadStats = useCallback(async () => {
    if (!challengeId) return;
    setLoadingStats(true);
    try {
      const { data } = await api.get(`/challenges/${challengeId}/analytics`);
      setStats(data);
    } catch (_) { /* silencioso */ } finally { setLoadingStats(false); }
  }, [challengeId]);

  useEffect(() => {
    if (open) {
      setTab("stats"); setSelected(new Set()); setSearch(""); setFeedback(null);
      setPhotos([]); setPhotosLoaded(false); setLightboxPhoto(null);
      loadStats();
    }
  }, [open, loadStats]);

  // Busca de alunos server-side (reaproveita ?q= + paginação)
  useEffect(() => {
    if (!open || tab !== "enroll" || !canEnroll) return;
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/users/students", { params: search.trim() ? { q: search.trim(), scope: "org", limit: 500 } : { scope: "org", limit: 500 } });
        setStudents(Array.isArray(data) ? data : []);
      } catch (_) { setStudents([]); }
    }, search.trim() ? 300 : 0);
    return () => clearTimeout(handle);
  }, [open, tab, search, canEnroll]);

  // Fotos de check-in do desafio — carrega uma vez ao entrar na aba
  useEffect(() => {
    if (!open || tab !== "photos" || photosLoaded || !challengeId || !canPhotos) return;
    setLoadingPhotos(true);
    api.get(`/challenges/${challengeId}/photos`)
      .then(({ data }) => setPhotos(Array.isArray(data) ? data : []))
      .catch(() => setPhotos([]))
      .finally(() => { setLoadingPhotos(false); setPhotosLoaded(true); });
  }, [open, tab, photosLoaded, challengeId, canPhotos]);

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const enroll = async () => {
    if (!selected.size) return;
    setEnrolling(true); setFeedback(null);
    try {
      const { data } = await api.post(`/challenges/${challengeId}/enroll`, { user_ids: Array.from(selected) });
      setFeedback(`Inscritos: ${data.enrolled} · já participavam: ${data.skipped}${data.invalid ? ` · ignorados: ${data.invalid}` : ""}.`);
      setSelected(new Set());
      loadStats();
    } catch (_) {
      setFeedback("Não foi possível inscrever. Tente novamente.");
    } finally { setEnrolling(false); }
  };

  const completionColor = useMemo(() => {
    const r = stats?.completion_rate ?? 0;
    return r >= 60 ? "#1d9e75" : r >= 30 ? "#f59e0b" : "var(--text)";
  }, [stats]);

  if (!open) return null;

  return createPortal(
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <strong style={{ fontSize: 16 }}>{challenge?.title || "Desafio"}</strong>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 20, cursor: "pointer" }}>×</button>
        </div>
        <p style={{ margin: "0 0 14px", fontSize: 13, color: "var(--muted)" }}>Acompanhe o desempenho e inscreva alunos.</p>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button style={tabBtn(tab === "stats")} onClick={() => setTab("stats")}>Desempenho</button>
          {canEnroll && (
            <button style={tabBtn(tab === "enroll")} onClick={() => setTab("enroll")}>Inscrever alunos</button>
          )}
          {canPhotos && (
            <button style={tabBtn(tab === "photos")} onClick={() => setTab("photos")}>Fotos</button>
          )}
        </div>

        {tab === "stats" && (
          <div>
            {loadingStats && <p style={{ color: "var(--muted)", fontSize: 13 }}>Carregando…</p>}
            {!loadingStats && stats && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Stat label="Participantes" value={stats.participants} />
                <Stat label="Concluíram" value={`${stats.completed} (${stats.completion_rate}%)`} accent={completionColor} />
                <Stat label="Progresso médio" value={`${stats.avg_progress_pct}%`} />
                <Stat label="Ativos" value={stats.active} accent="#1d9e75" />
                <Stat label="Perto da meta" value={stats.close_to_goal} accent="#f59e0b" />
                <Stat label="Parados (0)" value={stats.stalled} accent={stats.stalled ? "#ef4444" : "var(--text)"} />
              </div>
            )}
            {!loadingStats && stats && stats.participants === 0 && (
              <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 12 }}>
                Ninguém participa ainda. Use a aba "Inscrever alunos".
              </p>
            )}
          </div>
        )}

        {tab === "enroll" && canEnroll && (
          <div>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar aluno por nome ou email…"
              aria-label="Buscar aluno"
              style={{ ...inputStyle, marginBottom: 10 }}
            />
            <div style={{ maxHeight: 300, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
              {students.length === 0 && <p style={{ color: "var(--muted)", fontSize: 13 }}>Nenhum aluno encontrado.</p>}
              {students.map((u) => (
                <label key={u.id} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8,
                  background: selected.has(u.id) ? "rgba(29,158,117,0.12)" : "transparent", cursor: "pointer",
                }}>
                  <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} />
                  <span style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: 14 }}>{u.name}</span>
                    <span style={{ fontSize: 12, color: "var(--muted)" }}>{u.email}</span>
                  </span>
                </label>
              ))}
            </div>
            {feedback && <p style={{ fontSize: 13, color: "var(--text)", marginTop: 10 }}>{feedback}</p>}
            <button
              onClick={enroll}
              disabled={enrolling || selected.size === 0}
              style={{
                width: "100%", marginTop: 12, padding: "12px", borderRadius: 12, border: "none",
                background: selected.size ? "#1d9e75" : "rgba(255,255,255,0.08)",
                color: selected.size ? "#fff" : "var(--muted)", fontWeight: 700,
                cursor: selected.size ? "pointer" : "not-allowed",
              }}
            >
              {enrolling ? "Inscrevendo…" : `Inscrever ${selected.size} aluno(s)`}
            </button>
          </div>
        )}

        {tab === "photos" && canPhotos && (
          <div>
            {loadingPhotos && <p style={{ color: "var(--muted)", fontSize: 13 }}>Carregando…</p>}
            {!loadingPhotos && photos.length === 0 && (
              <p style={{ color: "var(--muted)", fontSize: 13 }}>
                Nenhuma foto de check-in enviada neste desafio ainda.
              </p>
            )}
            {!loadingPhotos && photos.length > 0 && (
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8,
                maxHeight: 380, overflowY: "auto",
              }}>
                {photos.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setLightboxPhoto(p)}
                    style={{
                      padding: 0, border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10,
                      overflow: "hidden", cursor: "pointer", background: "rgba(255,255,255,0.04)",
                      display: "flex", flexDirection: "column", textAlign: "left",
                    }}
                  >
                    <img
                      src={buildUploadUrl(p.photo_url)}
                      alt={`Check-in de ${p.user_name}`}
                      style={{ width: "100%", height: 96, objectFit: "cover", display: "block" }}
                      loading="lazy"
                    />
                    <span style={{ fontSize: 11, padding: "4px 6px", color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.user_name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {lightboxPhoto && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 1400, background: "rgba(0,0,0,0.82)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setLightboxPhoto(null); }}
        >
          <div style={{ maxWidth: 480, width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <strong style={{ color: "#fff", fontSize: 14 }}>{lightboxPhoto.user_name}</strong>
              <button
                onClick={() => setLightboxPhoto(null)}
                style={{ background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer" }}
              >×</button>
            </div>
            <img
              src={buildUploadUrl(lightboxPhoto.photo_url)}
              alt={`Check-in de ${lightboxPhoto.user_name}`}
              style={{ width: "100%", borderRadius: 12, display: "block" }}
            />
            <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, marginTop: 8 }}>
              {lightboxPhoto.exercise_name || "Check-in"}
              {lightboxPhoto.date ? ` · ${new Date(lightboxPhoto.date).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" })}` : ""}
              {typeof lightboxPhoto.points_awarded === "number" ? ` · +${lightboxPhoto.points_awarded} pts` : ""}
            </p>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
